import { Banner, Form, Heading, ToggleRadio } from '@boltenergy-be/design-system'
import { FormProvider, useForm } from 'react-hook-form'
import { PostalCodeFormInput } from './types'
import { CustomerFlows, DeliveryAddressFieldKeys, InputGroupKeys, PersonalDataFieldKeys } from 'store/customer/enums'
import { useTranslation } from 'next-i18next/pages'
import { nextStep, updateInput } from 'store/customer/slice'
import { useStoreDispatch, useStoreSelector } from 'hooks/store'
import { getProductName, getUsableProducts } from 'utils/products'
import { useRouter } from 'next/router'
import { setPostHogPersonProperties, trackEvent, trackGTMEvent } from 'utils/tracking'
import { PostalCodeAndTown } from 'components/inputs'
import SubmitButton from 'features/simulation/components/submit-button/SubmitButton'
import { useState } from 'react'
import { CUSTOMER_TYPES } from '@boltenergy-be/shared'
import { CommonTrackingParams } from 'types/tracking'

const PostalCodeStep = () => {
  // Redux store
  const { inputs, propositions } = useStoreSelector((store) => store.customer)
  const dispatch = useStoreDispatch()

  // Router
  const router = useRouter()

  // i18n
  const { t } = useTranslation(['simulation', 'common'])

  // Local state
  const [fetchingTowns, setFetchingTowns] = useState<boolean>(false)

  // REACT HOOK FORM
  const hookForm = useForm<PostalCodeFormInput>({
    defaultValues: {
      userType: inputs.personalData.isCompany ? CUSTOMER_TYPES.professional : CUSTOMER_TYPES.residential,
      postalCode: inputs.deliveryAddress.address.postalCode,
      townCode: inputs.deliveryAddress.address.townCode,
      townName: inputs.deliveryAddress.address.townName
    },
    mode: 'onChange'
  })
  const {
    formState: { errors },
    handleSubmit,
    watch,
    register
  } = hookForm
  const watchPostalCode = watch('postalCode')

  // Constants
  const usableProducts = inputs.simulation.region ? getUsableProducts({ region: inputs.simulation.region }) : null
  const selectedProductNotAvailableInCurrentRegion =
    !!watchPostalCode &&
    usableProducts &&
    propositions.simulateSpecificProduct &&
    !usableProducts.includes(propositions.simulateSpecificProduct)

  /**
   * Handles the form submit after validation by React Hook Form
   */
  const onSubmit = (data: PostalCodeFormInput) => {
    const { userType, postalCode, townCode, townName } = data

    // Store region for the proposition-step A/B test without identifying the visitor.
    setPostHogPersonProperties({
      [CommonTrackingParams.REGION]: inputs.simulation.region
    })

    dispatch(
      updateInput({
        group: InputGroupKeys.PERSONAL_DATA,
        key: PersonalDataFieldKeys.IS_COMPANY,
        value: userType === CUSTOMER_TYPES.professional
      })
    )
    dispatch(
      updateInput({
        group: InputGroupKeys.DELIVERY_ADDRESS,
        key: DeliveryAddressFieldKeys.ADDRESS,
        value: {
          ...inputs[InputGroupKeys.DELIVERY_ADDRESS][DeliveryAddressFieldKeys.ADDRESS],
          postalCode,
          townCode,
          townName
        }
      })
    )

    // Track 'Simulation step finished' event @Semetis
    trackGTMEvent('simulation_step_finished:postal-code', {
      postcode: postalCode
    })
    trackEvent({ category: 'simulation', object: 'postal_code_step', action: 'finish' })

    dispatch(nextStep({ flow: CustomerFlows.SIMULATION }))
  }

  return (
    <>
      <Heading as="h1" variant="h3" serif>
        {t('simulation:steps.postalCode.title')}
      </Heading>

      <FormProvider {...hookForm}>
        <Form onSubmit={handleSubmit(onSubmit)}>
          <ToggleRadio
            label={t('simulation:fields.clientType.label')}
            grid="full"
            name="userType"
            error={errors?.userType?.message}
            options={[
              {
                ...register('userType', { required: t('validation:required') }),
                value: CUSTOMER_TYPES.residential,
                id: CUSTOMER_TYPES.residential,
                label: t('common:residential')
              },
              {
                ...register('userType', { required: t('validation:required') }),
                value: CUSTOMER_TYPES.professional,
                id: CUSTOMER_TYPES.professional,
                label: t('common:company')
              }
            ]}
          />

          <PostalCodeAndTown grid="full" hideTownSelectField {...{ setFetchingTowns }} />

          {selectedProductNotAvailableInCurrentRegion && (
            <Banner
              title={t('simulation:steps.postalCode.alerts.notAvailableInRegion', {
                products: usableProducts.map((product) => `Bolt ${getProductName(product, router.locale)}`).join(', ')
              })}
              type="warning"
            />
          )}

          <Form.Footer>
            <SubmitButton loading={fetchingTowns} disabled={'postalCode' in errors || !watchPostalCode} />
          </Form.Footer>
        </Form>
      </FormProvider>
    </>
  )
}

export default PostalCodeStep
